import React, { useState, useMemo, useEffect, useCallback } from "react";

import {
  reportError,
  trackEvent,
  NotificationStatuses,
  BLACK_BOX_ELEMENT_ID,
} from "@jsainsburyplc/id-ui-common";

import { otpService } from "services/otp.service";
import { getErrorMessage } from "utils/otpForm";
import { categoryTypes, actionTypes } from "constants/tracking";
import {
  EMAIL_PREFERENCE_VALUE,
  MOBILE_PREFERENCE_VALUE,
  DATA_CUSTOMER_INFO,
  DATA_ERROR,
} from "constants/login";
import {
  newCodeMessage,
  newCodeErrorMessage,
  OTP_FORM_ID,
  OTP,
  MFA_CODE_SENT,
  MFA_CUSTOMER_INFO,
  MFA_LOGIN_CHALLENGE,
  MFA_TOKEN_COOKIE_MISSING,
  mfaCookieErrorMessage,
} from "constants/mfaAndOtp";
import {
  redirectToLoginForMissingMfaToken,
  resetMissingMfaTokenGuard,
} from "utils/mfaSession";
import { getBlackBoxToken } from "utils/iovation";
import { getEnv } from "utils/env";
import { isIovationEnabled } from "utils/env";

import OtpForm from "components/OtpForm";
import PageFormat from "components/PageFormat";

const parseCustomerInfo = (customerInfoValue) => {
  const defaultCustomerInfo = { email: "", mobile: "" };
  try {
    return (
      JSON.parse(decodeURIComponent(customerInfoValue)) || defaultCustomerInfo
    );
  } catch (e) {
    return defaultCustomerInfo;
  }
};

const trackRequestCode = (action) => {
  trackEvent({
    category: categoryTypes.MFA,
    label: "Request new MFA code",
    action,
  });
};

const trackFormSubmit = () => {
  trackEvent({
    category: categoryTypes.MFA,
    action: actionTypes.SUBMIT,
    label: "Submit OTP code",
  });
};

const trackFormSubmitError = (code) => {
  trackEvent({
    category: categoryTypes.MFA,
    action: actionTypes.ERROR,
    label: `Submit OTP code: ${code}`,
  });
};

const getAndAssignBlackboxToken = (form) => {
  try {
    form[BLACK_BOX_ELEMENT_ID].value = getBlackBoxToken(OTP_FORM_ID);
  } catch {
    reportError(`${OTP_FORM_ID} - the blackbox form field is not present`);
  }
};

const OtpPage = () => {
  const rootElem = document.querySelector("#root");
  const customerInfoValue = rootElem.getAttribute(DATA_CUSTOMER_INFO);
  // In the new split-frontend architecture id-account-auth redirects back to
  // the SPA with ?error=<code> instead of rendering a Thymeleaf view that sets
  // the data-error HTML attribute.  Support both mechanisms so the page works
  // whether it was reached via a Thymeleaf render (legacy) or a PRG redirect.
  const errorCode = parseInt(
    rootElem.getAttribute(DATA_ERROR) ||
      new URLSearchParams(window.location.search).get("error")
  );

  const [requestingNewCode, setRequestingNewCode] = useState(false);
  const [message, setMessage] = useState(
    errorCode ? getErrorMessage(errorCode) : undefined
  );
  // customerInfo is populated either from the legacy Thymeleaf data-customer-info
  // attribute OR from the send-mfa JSON response (new split-frontend flow).
  // It is also persisted to sessionStorage so it survives the PRG redirect that
  // happens when an OTP code is incorrect (after which requestCode is not
  // re-called because MFA_CODE_SENT is already "true" in sessionStorage).
  const [customerInfo, setCustomerInfo] = useState(() =>
    parseCustomerInfo(
      customerInfoValue || sessionStorage.getItem(MFA_CUSTOMER_INFO)
    )
  );

  const parsedCustomerInfo = useMemo(() => customerInfo, [customerInfo]);

  //  const preference = !parsedCustomerInfo.mobile
  //    ? EMAIL_PREFERENCE_VALUE
  //    : MOBILE_PREFERENCE_VALUE;
  //
  //  const preferenceInfo =
  //    preference === MOBILE_PREFERENCE_VALUE
  //      ? parsedCustomerInfo.mobile
  //      : parsedCustomerInfo.email;

  const preference = EMAIL_PREFERENCE_VALUE;
  const preferenceInfo = parsedCustomerInfo.email;

  // Formik calls onSubmit(values, helpers) after validation passes.
  // Receiving `values` here is the key fix: NumberInputField (@sainsburys-tech/
  // luna-form) is a React controlled component — Formik holds the OTP in its
  // state but the underlying DOM <input> may NOT carry a `name` attribute, so
  // a native form.submit() would POST an empty `code` field to id-account-auth,
  // which the MFA service then rejects as invalid (error 6064) even when the
  // user typed the correct OTP.
  // By injecting a hidden <input name="code"> with the Formik value we guarantee
  // the correct OTP reaches the backend via the native POST regardless of how
  // NumberInputField renders its inner DOM.
  const submitHandler = (values) => {
    const form = document.getElementById(OTP_FORM_ID);
    isIovationEnabled() && getAndAssignBlackboxToken(form);

    // Ensure the OTP code is included in the native form submission.
    let codeInput = form.querySelector(`input[name="${OTP}"]`);
    if (!codeInput) {
      codeInput = document.createElement("input");
      codeInput.type = "hidden";
      codeInput.name = OTP;
      form.appendChild(codeInput);
    }
    codeInput.value = (values && values[OTP]) || "";

    // The SPA is served under /login-ui/*, but the backend OTP validation endpoint
    // lives at /{missionId}/login/mfa (proxied by nginx to id-account-auth).
    // Strip the /login-ui prefix so the native form POST reaches the backend,
    // not the nginx static-file handler which only accepts GET.
    // Also strip the ?error= param that was appended by a previous PRG redirect
    // so it does not accumulate on successive failures or get forwarded to the
    // backend (id-account-auth doesn't use it and would re-append it on the next
    // redirect, causing ?error=6064&error=6064&... to grow without bound).
    const params = new URLSearchParams(window.location.search);
    params.delete("error");
    const cleanSearch = params.toString() ? "?" + params.toString() : "";
    const backendPath =
      window.location.pathname.replace(/^\/login-ui/, "") + cleanSearch;
    form.setAttribute("action", backendPath);
    trackFormSubmit();
    form.submit();
  };

  const requestCode = useCallback(async (showSuccessMessage) => {
    setRequestingNewCode(true);
    try {
      const response = await otpService.requestOtp();
      if (response.ok) {
        resetMissingMfaTokenGuard();
        sessionStorage.setItem(MFA_CODE_SENT, "true");
        setRequestingNewCode(false);
        trackRequestCode(actionTypes.SUCCESS);
        // Populate customerInfo from the send-mfa JSON response so the page
        // can display where the code was sent (masked email / mobile) in the
        // new split-frontend flow where no Thymeleaf attribute is available.
        // Persist to sessionStorage so it survives the PRG redirect on error.
        if (response.customerInfo) {
          sessionStorage.setItem(MFA_CUSTOMER_INFO, response.customerInfo);
          setCustomerInfo(parseCustomerInfo(response.customerInfo));
        }
        if (showSuccessMessage) {
          setMessage({
            type: NotificationStatuses.SUCCESS_TYPE,
            text: newCodeMessage,
          });
        }
      } else if (response.errorMessage === MFA_TOKEN_COOKIE_MISSING) {
        trackRequestCode(actionTypes.ERROR);
        if (redirectToLoginForMissingMfaToken(response.recoveryId)) {
          reportError(
            "OtpPage - send-mfa rejected: mfa token cookie missing, redirecting to login"
          );
          return;
        }
        // Already sent back to login once and the cookie is still missing:
        // the browser is not keeping cookies, so stop here instead of looping.
        reportError(
          "OtpPage - send-mfa rejected: mfa token cookie still missing after re-login, not redirecting"
        );
        setRequestingNewCode(false);
        setMessage({
          type: NotificationStatuses.ERROR_TYPE,
          text: mfaCookieErrorMessage,
        });
        return;
      } else {
        // Record the actual HTTP status of the failure. Previously this was
        // discarded (thrown as a generic `Error`), so there was no way to
        // tell - after the fact, for a specific customer - whether the
        // send-otp call reached the backend and was rejected (e.g. missing
        // mfa_token cookie -> 400, invalid hydra session -> 401) versus never
        // being sent at all.
        reportError(
          `OtpPage - send-mfa request rejected by backend: status=${response.status}`
        );
        throw Error;
      }
    } catch (error) {
      setRequestingNewCode(false);
      trackRequestCode(actionTypes.ERROR);
      reportError(`OtpPage - send-mfa request failed: ${error}`);
      setMessage({
        type: NotificationStatuses.ERROR_TYPE,
        text: newCodeErrorMessage,
      });
    }
  }, []);

  const handleRequestNewCode = async (event) => {
    event && event.preventDefault();
    trackRequestCode(actionTypes.CODE_REQUESTED);
    requestCode(true);
  };

  useEffect(() => {
    // Guard against stale sessionStorage from a previous login flow.
    // The OAuth2 login_challenge is unique per flow; when it differs from the
    // one stored in sessionStorage the user has started a brand-new login
    // attempt (e.g. closed the tab and re-opened, or retried from the RP).
    // In that case we must send a fresh OTP — the code in the database belongs
    // to the old flow and will fail validation even if it looks "correct".
    const urlParams = new URLSearchParams(window.location.search);
    const currentChallenge = urlParams.get("login_challenge") || "";
    const storedChallenge = sessionStorage.getItem(MFA_LOGIN_CHALLENGE) || "";

    if (currentChallenge !== storedChallenge) {
      // New flow — purge all stale MFA session state and record the new challenge.
      sessionStorage.removeItem(MFA_CODE_SENT);
      sessionStorage.removeItem(MFA_CUSTOMER_INFO);
      sessionStorage.setItem(MFA_LOGIN_CHALLENGE, currentChallenge);
    }

    const isCodeSent = sessionStorage.getItem(MFA_CODE_SENT) === "true";
    if (isCodeSent) {
      // This is the silent-drop path: if login_challenge is missing/empty on
      // this redirect (or was reused) AND a stale MFA_CODE_SENT="true" flag
      // is still present, the OTP send request is never fired at all - no
      // network call, no backend log, nothing in id-mfa-service. Record this
      // explicitly so it is visible in client-side telemetry even though the
      // backend has zero trace of it.
      trackRequestCode(actionTypes.AUTO_SEND_SKIPPED);
      reportError(
        `OtpPage - send-mfa auto-send skipped: currentChallenge="${currentChallenge}" storedChallenge="${storedChallenge}"`
      );
    } else {
      requestCode();
    }
  }, [requestCode]);

  useEffect(() => {
    if (message) {
      trackFormSubmitError(errorCode);
    }
  }, [message, errorCode]);

  return (
    <PageFormat>
      <div className="c-hero c-hero--center">
        <h1>We've sent you a code</h1>
      </div>
      <p className="ln-u-margin-bottom*3">
        We just need to verify your details. We've sent a verification code to:
      </p>
      <p className="ln-u-margin-bottom*3 ln-u-font-weight-bold">
        {preferenceInfo}
      </p>
      <p>Please enter the code to continue to log in:</p>

      <OtpForm
        onRequestCode={handleRequestNewCode}
        onSubmit={submitHandler}
        isRequestingCode={requestingNewCode}
        notification={message}
        isIovationEnabled={getEnv("REACT_APP_IOVATION_ENABLED")}
      />
    </PageFormat>
  );
};

export default OtpPage;
